import React, { useState, useCallback } from 'react';
import { Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CreateFeedbackForm, FeedbackCategory } from '@/types/feedback';
import { cn } from '@/lib/utils';

interface FeedbackSubmissionProps {
  onSuccess?: (feedbackId: number, slug: string) => void;
  onCancel?: () => void;
  className?: string;
}

const FeedbackSubmission: React.FC<FeedbackSubmissionProps> = ({
  onSuccess,
  onCancel,
  className,
}) => {
  const [form, setForm] = useState<CreateFeedbackForm>({
    title: '',
    description: '',
    category_id: 0,
    attachments: [],
  });
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();

  // Load categories
  React.useEffect(() => {
    const loadCategories = async () => {
      const { data, error } = await supabase
        .from('feedback_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) {
        console.error('Failed to load categories:', error);
        return;
      }

      setCategories(data || []);
      // Set default category
      if (data && data.length > 0) {
        setForm(prev => ({ ...prev, category_id: data[0].id }));
      }
    };

    loadCategories();
  }, []);

  const handleInputChange = useCallback((field: keyof CreateFeedbackForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const validFiles = Array.from(files).filter(file => {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: `${file.name} is larger than 10MB`,
          variant: 'destructive',
        });
        return false;
      }

      // Check file type
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf', 'text/plain', 'video/mp4', 'video/webm'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Unsupported file type',
          description: `${file.name} is not a supported file type`,
          variant: 'destructive',
        });
        return false;
      }

      return true;
    });

    setForm(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...validFiles],
    }));
  }, [toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const removeAttachment = useCallback((index: number) => {
    setForm(prev => ({
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index) || [],
    }));
  }, []);

  const validateForm = (): string | null => {
    if (!form.title.trim()) return 'Title is required';
    if (form.title.trim().length < 5) return 'Title must be at least 5 characters';
    if (!form.description.trim()) return 'Description is required';
    if (form.description.trim().length < 10) return 'Description must be at least 10 characters';
    if (!form.category_id) return 'Please select a category';
    return null;
  };

  const uploadAttachments = async (feedbackId: number): Promise<string[]> => {
    if (!form.attachments || form.attachments.length === 0) return [];

    const uploadPromises = form.attachments.map(async (file, index) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${index}.${fileExt}`;
      const filePath = `${user?.id}/${feedbackId}/${fileName}`;

      const { error } = await supabase.storage
        .from('feedback-attachments')
        .upload(filePath, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('feedback-attachments')
        .getPublicUrl(filePath);

      return publicUrl;
    });

    return Promise.all(uploadPromises);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to submit feedback',
        variant: 'destructive',
      });
      return;
    }

    const validation = validateForm();
    if (validation) {
      toast({
        title: 'Validation error',
        description: validation,
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Create feedback item
      const { data, error } = await supabase.rpc('create_feedback_item', {
        p_title: form.title.trim(),
        p_description: form.description.trim(),
        p_category_id: form.category_id,
      });

      if (error) throw error;

      if (!data[0]?.success) {
        throw new Error(data[0]?.message || 'Failed to create feedback');
      }

      const feedbackId = data[0].feedback_id;
      const slug = data[0].slug;

      // Upload attachments if any
      if (form.attachments && form.attachments.length > 0) {
        await uploadAttachments(feedbackId);
      }

      toast({
        title: 'Feedback submitted',
        description: data[0].message,
      });

      // Reset form
      setForm({
        title: '',
        description: '',
        category_id: categories[0]?.id || 0,
        attachments: [],
      });

      if (onSuccess) {
        onSuccess(feedbackId, slug);
      }
    } catch (error: any) {
      toast({
        title: 'Failed to submit feedback',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <ImageIcon className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card className={cn('w-full max-w-2xl', className)}>
      <CardHeader>
        <CardTitle>Submit Feedback</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            placeholder="Brief description of your feedback"
            value={form.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            maxLength={100}
          />
          <p className="text-xs text-muted-foreground">
            {form.title.length}/100 characters
          </p>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <Select
            value={form.category_id.toString()}
            onValueChange={(value) => handleInputChange('category_id', parseInt(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id.toString()}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    {category.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            placeholder="Provide detailed information about your feedback, including steps to reproduce for bugs or use cases for features."
            value={form.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={5}
            maxLength={2000}
          />
          <p className="text-xs text-muted-foreground">
            {form.description.length}/2000 characters
          </p>
        </div>

        {/* File Upload */}
        <div className="space-y-2">
          <Label>Attachments (Optional)</Label>
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
              isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
              'hover:border-primary/50 hover:bg-primary/5'
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Drag & drop files here, or{' '}
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto font-normal"
                onClick={() => document.getElementById('file-input')?.click()}
              >
                browse
              </Button>
            </p>
            <p className="text-xs text-muted-foreground">
              Supports: Images, PDFs, videos (max 10MB each)
            </p>
            <input
              id="file-input"
              type="file"
              multiple
              accept="image/*,application/pdf,text/plain,video/mp4,video/webm"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </div>

          {/* Attachment List */}
          {form.attachments && form.attachments.length > 0 && (
            <div className="space-y-2">
              {form.attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-2 border rounded-lg bg-muted/50"
                >
                  {getFileIcon(file)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAttachment(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? 'Submitting...' : 'Submit Feedback'}
          </Button>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Your feedback will be reviewed before being published. You can edit it until it's published.
        </p>
      </CardContent>
    </Card>
  );
};

export default FeedbackSubmission;